import type { NativeContextRNProps } from '../../props/NativeContextRNProps';

import { ThemeProvider } from '../../../mads/ThemeProvider';
import { globalErrorHandler } from '../../../viewjob/src/util/globalErrorHandler';
import { DatadogLogger } from '../../logging/DatadogLogger';
import { getDeviceProperties } from '../../logging/LoggableDeviceProperties';
import { RenderErrorBoundary } from '../../logging/RenderErrorBoundary';
import { logScreenView } from '../../native-modules/tare/logScreenView';
import { CodePushDialog } from '../CodePushDialog';
import { ReportErrorProvider } from '../ReportErrorProvider';

import { log as logProviderRendered } from '@indeed/signals-schema/com/indeed/signals/schema/reactnative/RNProviderRenderedV1';
import React, { useEffect } from 'react';

export interface ProviderRootProps {
    nativeContext?: NativeContextRNProps;
}

/**
 * Wraps an RN root component with shared provider/tracking behavior.
 *
 * @param WrappedComponent The RN root to wrap.
 * @param providerId Identifier for the provider; used for Datadog views and provider-rendered signals.
 * @param screenName JSMA screen name to log on mount. Defaults to `providerId`. Pass `null` to skip
 *   the auto screen-view (e.g. when the root fires its own screen-view with richer extras).
 */
function withProviderTracking<P>(
    WrappedComponent: React.ComponentType<P & ProviderRootProps>,
    providerId: string,
    screenName: string | null = providerId,
) {
    const ProviderRootComponent = (props: P & ProviderRootProps) => {
        useEffect(() => {
            logProviderRendered({
                provider: providerId,
                deviceProperties: getDeviceProperties(),
            });

            void DatadogLogger.startView(providerId);
            if (screenName !== null) {
                logScreenView(screenName).catch(console.error);
            }
            return () => {
                void DatadogLogger.stopView(providerId);
            };
        }, []);

        globalErrorHandler.addProviderId(providerId);

        return (
            <ThemeProvider>
                <RenderErrorBoundary provider={providerId}>
                    <ReportErrorProvider providerId={providerId}>
                        <WrappedComponent {...props} />
                        <CodePushDialog />
                    </ReportErrorProvider>
                </RenderErrorBoundary>
            </ThemeProvider>
        );
    };

    ProviderRootComponent.displayName = `WithProviderTracking(${WrappedComponent.displayName || WrappedComponent.name})`;

    return ProviderRootComponent;
}

export default withProviderTracking;
